import { create } from 'zustand';
import {
  Phase,
  FileStatus,
  GroupType,
  UploadHubView,
  TemplateType,
  ExtractionStatus,
  LOG_STATUS_BY_FILE_STATUS,
} from '../constants';
import { canGroup } from '../utils/groupingUtils';
import i18n from 'i18n';

// Mirrors NavSidebar's collapse persistence (NavSidebar.jsx:43,477). Reads are
// guarded because the store module is imported by unit tests that run without
// a DOM, and by SSR-ish tooling where localStorage is absent.
export const RAIL_COLLAPSE_KEY = 'uploadHubRailCollapsed';

/**
 * The batch this tab is working on, so a reload can reopen it (D48).
 *
 * `sessionStorage`, not `localStorage`: per tab on purpose — a second tab that
 * starts a new upload must not be pulled into this one's batch. Guarded like
 * the rail key: unit tests and private windows can have no storage at all.
 */
export const OPEN_BATCH_KEY = 'uploadHubOpenBatchId';

export const readOpenBatchId = () => {
  try {
    return window.sessionStorage.getItem(OPEN_BATCH_KEY) || null;
  } catch {
    return null;
  }
};

export const writeOpenBatchId = (batchId) => {
  try {
    if (batchId) window.sessionStorage.setItem(OPEN_BATCH_KEY, batchId);
    else window.sessionStorage.removeItem(OPEN_BATCH_KEY);
  } catch {
    // No storage: a reload lands on the dropzone, as it always did.
  }
};

const readRailCollapsed = () => {
  try {
    const saved = window.localStorage.getItem(RAIL_COLLAPSE_KEY);
    return saved === null ? false : JSON.parse(saved);
  } catch {
    return false;
  }
};

const writeRailCollapsed = (value) => {
  try {
    window.localStorage.setItem(RAIL_COLLAPSE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable (private mode, no DOM) — in-memory state still works */
  }
};

// --- Customs (PDF) helpers -------------------------------------------------
//
// A customs file arrives from `/upload-hub/detect` *before* its extraction has
// run (D4): the response only carries `extractionStatus: 'pending'`. The full
// `{ status, data, model, usage, startedAt, finishedAt, error }` object lands
// later, either from the poll hook or from `detectionCache.customsExtraction`
// on resume (I6). Both paths funnel through these two helpers so ingestion,
// merge and hydrate can never drift apart.

/**
 * A READY extraction of a document that is not an import declaration is a
 * failure, not lines to import (UX review 18, D70). The extractor reads the
 * page's own form and answers `docType: 'other'` for an export declaration or
 * an invoice; its "goods" used to go straight into the review as imports.
 * Only `'other'` is refused — a legacy extraction has no `docType` at all.
 */
export const NOT_AN_IMPORT_DECLARATION = 'NOT_AN_IMPORT_DECLARATION';
export const refuseNonImportDocument = (extraction) => {
  if (extraction?.status !== ExtractionStatus.READY || extraction.data?.docType !== 'other') {
    return extraction;
  }
  return { ...extraction, status: ExtractionStatus.ERROR, errorCode: NOT_AN_IMPORT_DECLARATION };
};

const normalizeExtraction = (result) => {
  if (result?.extraction) return refuseNonImportDocument(result.extraction);
  if (result?.customsExtraction) return refuseNonImportDocument(result.customsExtraction);
  if (result?.extractionStatus) return { status: result.extractionStatus };
  return null;
};

/**
 * Initial `FileStatus` for a freshly detected file. Customs PDFs whose
 * extraction is still running get PARSING (not ERROR — an unresolved customs
 * file is a perfectly normal intermediate state, unlike an unknown template).
 */
const initialFileStatus = (templateType, extraction) => {
  if (templateType === TemplateType.CUSTOMS) {
    if (extraction?.status === ExtractionStatus.ERROR) return FileStatus.ERROR;
    if (extraction?.status === ExtractionStatus.READY) return FileStatus.PENDING;
    return FileStatus.PARSING;
  }
  return templateType === TemplateType.UNKNOWN ? FileStatus.ERROR : FileStatus.PENDING;
};

/**
 * Empty `customsBatch` slice (D14/D16).
 *
 * The consolidated customs review is batch-level, so its working state cannot
 * live on a file. It is *persisted* per file all the same (`resolutionState.
 * customs`, see `CustomsBatchWizard`), which is why nothing here needs a new
 * backend field — `hydrateFromBatch` still rebuilds from the per-file records
 * (I6) and this slice is re-seeded from them.
 */
const emptyCustomsBatch = () => ({
  step: null,
  rows: null,
  supplierResolutions: {},
  // Per resolved supplier: `{ installationId, installationInternalId, installationName,
  // countryId }` — or absent, which means "no installation" (D20's default).
  // `installationsSkipped` records that the user skipped the step as a whole,
  // so the stepper can mark it `skipped` rather than merely unvisited.
  installationResolutions: {},
  installationsSkipped: false,
  // Row ids the user skipped by hand from the grid's Status column (D35).
  // Persisted per file with the rest of the review (D41) — never re-derived.
  excludedRowIds: [],
  backendErrors: null,
  submitted: null,
});
/** An upload log's server status → the hub file status it reopens as. Anything else is pending. */
const FILE_STATUS_BY_LOG_STATUS = Object.fromEntries(
  Object.entries(LOG_STATUS_BY_FILE_STATUS).map(([fileStatus, logStatus]) => [
    logStatus,
    fileStatus,
  ]),
);

const useUploadHubStore = create((set, get) => ({
  phase: Phase.DROPZONE,
  view: UploadHubView.UPLOAD,
  batchId: null,

  // Consolidated customs review (Phase.CUSTOMS_BATCH)
  customsBatch: emptyCustomsBatch(),

  files: {},
  fileOrder: [],

  groups: {},
  groupOrder: [],

  activeFileId: null,
  isDetecting: false,

  // The customs review's autosave, as the user should see it (D49):
  // `idle` | `pending` (debounce waiting) | `saving` | `saved` | `error`.
  // Here, not in the hook, so a save flushed after the wizard unmounts still
  // reports, and the page's leave guard can read it (D50).
  customsSave: { status: 'idle', at: null },
  setCustomsSave: (customsSave) => set({ customsSave }),

  // --- UI state ---
  // The files rail collapses to an icon dock so the wizard it sits next to
  // gets the width its column grids need. Persisted like the app's own nav
  // sidebar (NavSidebar.jsx COLLAPSE_KEY) so the choice survives a reload.
  railCollapsed: readRailCollapsed(),

  // The transcript's "the goods are below" jump, retired for the rest of this
  // upload session once the user has reached the goods table — by clicking it
  // or by scrolling there themselves. Store state rather than a module flag or
  // component state: the rail unmounts the panel on every declaration switch,
  // so component state would re-offer it on the next file, and a module flag
  // would outlive the session. Living here means `reset()` clears it exactly
  // when a new upload session begins, which is the definition that was asked
  // for.
  goodsJumpDismissed: false,

  // --- Phase & View ---
  setPhase: (phase) => set({ phase }),
  setView: (view) => set({ view }),
  setBatchId: (batchId) => {
    writeOpenBatchId(batchId);
    set({ batchId });
  },

  dismissGoodsJump: () => set({ goodsJumpDismissed: true }),

  // --- Detection Results ---
  setDetectionResults: (results, rawFilesMap) => {
    const files = {};
    const fileOrder = [];

    for (const result of results) {
      const fileId =
        result.uploadLogId ||
        result._id ||
        result.id ||
        `file_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const filename = result.filename || result.originalName || result.name || 'unknown';
      const templateType = result.templateType || 'unknown';
      const extraction = normalizeExtraction(result);
      files[fileId] = {
        fileId,
        filename,
        templateType,
        detectedSheets: result.detectedSheets || null,
        extractedEntity: result.extractedEntity || null,
        parsedData: result.parsedData || null,
        error: result.error || null,
        rawFile: rawFilesMap[filename] || rawFilesMap[result.originalName] || null,
        status: initialFileStatus(templateType, extraction),

        // Customs (PDF) state
        extraction,
        // The stale guard's clock when the backend recorded no `startedAt` (D71).
        firstSeenAt: Date.now(),
        customsState: null,

        // EFDA/COMMS resolution state
        resolvedSupplierId: null,
        resolvedSupplierName: null,
        resolvedInstallationId: null,
        resolvedInstallationName: null,
        isNewSupplier: false,
        isNewInstallation: false,
        updateExistingFields: false,

        // CG resolution state
        cgSheetProgress: result.detectedSheets
          ? Object.fromEntries(result.detectedSheets.map((s) => [s.mappedSheetName, 'pending']))
          : null,

        // Upload result (EFDA/COMMS)
        uploadResult: null,

        // Grouping
        groupId: null,
      };
      fileOrder.push(fileId);
    }

    const groupableCount = Object.values(files).filter((f) => canGroup(f)).length;
    set({
      files,
      fileOrder,
      activeFileId: fileOrder[0] || null,
      phase: groupableCount >= 2 ? Phase.ORGANIZE : Phase.RESOLUTION,
      isDetecting: false,
      customsBatch: emptyCustomsBatch(),
    });
  },

  mergeDetectionResults: (results, rawFilesMap) => {
    const { files, fileOrder } = get();
    const newFiles = { ...files };
    const newFileOrder = [...fileOrder];
    let addedCustoms = false;

    for (const result of results) {
      const fileId =
        result.uploadLogId ||
        result._id ||
        result.id ||
        `file_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      if (newFiles[fileId]) continue; // skip duplicates

      const filename = result.filename || result.originalName || result.name || 'unknown';
      const templateType = result.templateType || 'unknown';
      const extraction = normalizeExtraction(result);
      if (templateType === TemplateType.CUSTOMS) addedCustoms = true;
      newFiles[fileId] = {
        fileId,
        filename,
        templateType,
        detectedSheets: result.detectedSheets || null,
        extractedEntity: result.extractedEntity || null,
        parsedData: result.parsedData || null,
        error: result.error || null,
        rawFile: rawFilesMap[filename] || rawFilesMap[result.originalName] || null,
        status: initialFileStatus(templateType, extraction),
        extraction,
        // The stale guard's clock when the backend recorded no `startedAt` (D71).
        firstSeenAt: Date.now(),
        customsState: null,
        resolvedSupplierId: null,
        resolvedSupplierName: null,
        resolvedInstallationId: null,
        resolvedInstallationName: null,
        isNewSupplier: false,
        isNewInstallation: false,
        updateExistingFields: false,
        cgSheetProgress: result.detectedSheets
          ? Object.fromEntries(result.detectedSheets.map((s) => [s.mappedSheetName, 'pending']))
          : null,
        uploadResult: null,
        groupId: null,
      };
      newFileOrder.push(fileId);
    }

    // A declaration added after the batch grid was seeded has to make it into
    // that grid. Dropping the seed is safe: re-seeding prefers each file's
    // persisted `resolutionState.customs` rows, so edits already made to the
    // earlier declarations survive.
    set({
      files: newFiles,
      fileOrder: newFileOrder,
      isDetecting: false,
      ...(addedCustoms ? { customsBatch: emptyCustomsBatch() } : {}),
    });
  },

  // --- Active File ---
  setActiveFile: (fileId) => set({ activeFileId: fileId }),

  /**
   * After `fromFileId` is resolved: open the next file still to resolve, or the
   * summary when none is left.
   */
  advanceToNextFile: (fromFileId) => {
    const { files, fileOrder } = get();
    const next = fileOrder.find(
      (fid) =>
        fid !== fromFileId &&
        files[fid]?.status !== FileStatus.COMPLETED &&
        files[fid]?.status !== FileStatus.SKIPPED,
    );
    set(next ? { activeFileId: next } : { phase: Phase.SUMMARY });
  },

  // --- File Status ---
  updateFileStatus: (fileId, status) =>
    set((state) => ({
      files: {
        ...state.files,
        [fileId]: { ...state.files[fileId], status },
      },
    })),

  // --- Customs extraction (poll result) ---
  // Only ever writes `extraction` + the PARSING/PENDING/ERROR *file* status.
  // `processingStatus` is untouched here and everywhere else outside the
  // phase-4 finalize step (D5/I1). A file the user already resolved keeps its
  // status — a late poll tick must not resurrect a completed file.
  setFileExtraction: (fileId, incoming) =>
    set((state) => {
      const file = state.files[fileId];
      if (!file || !incoming) return state;
      const extraction = refuseNonImportDocument(incoming);

      const isTerminalResolution =
        file.status === FileStatus.COMPLETED || file.status === FileStatus.SKIPPED;

      let status = file.status;
      if (!isTerminalResolution) {
        if (extraction.status === ExtractionStatus.PENDING) status = FileStatus.PARSING;
        else if (extraction.status === ExtractionStatus.ERROR) status = FileStatus.ERROR;
        else if (file.status === FileStatus.PARSING) status = FileStatus.PENDING;
      }

      // When it settled, for the batch's time-left estimate (D71). Cleared
      // when a retry puts it back to pending.
      const wasPending = file.extraction?.status === ExtractionStatus.PENDING;
      const settledAt =
        extraction.status === ExtractionStatus.PENDING
          ? null
          : wasPending
            ? Date.now()
            : (file.settledAt ?? null);

      return {
        files: {
          ...state.files,
          [fileId]: { ...file, extraction, status, settledAt },
        },
      };
    }),

  // How many extraction retries this session made for a file (D60). The
  // failure copy stops offering Retry after `MAX_EXTRACTION_RETRIES` and points
  // at support instead: a retry that has failed three times will fail a fourth.
  countExtractionRetry: (fileId) =>
    set((state) => {
      const file = state.files[fileId];
      if (!file) return state;
      return {
        files: {
          ...state.files,
          [fileId]: { ...file, extractionRetries: (file.extractionRetries ?? 0) + 1 },
        },
      };
    }),

  // --- Consolidated customs batch state (D14) ---
  // Patch-merge so callers can update one slice (`{ step }`, `{ rows }`, …)
  // without having to restate the rest.
  setCustomsBatch: (patch) =>
    set((state) => ({ customsBatch: { ...state.customsBatch, ...patch } })),

  resetCustomsBatch: () => set({ customsBatch: emptyCustomsBatch() }),

  // --- Customs resolution state (`resolutionState.customs`) ---
  setCustomsState: (fileId, customsState) =>
    set((state) => {
      const file = state.files[fileId];
      if (!file) return state;
      return {
        files: {
          ...state.files,
          [fileId]: { ...file, customsState },
        },
      };
    }),

  // --- File Resolution ---
  updateFileResolution: (fileId, updates) =>
    set((state) => ({
      files: {
        ...state.files,
        [fileId]: { ...state.files[fileId], ...updates },
      },
    })),

  // --- CG Sheet Progress ---
  updateCgSheetProgress: (fileId, sheetName, status) =>
    set((state) => ({
      files: {
        ...state.files,
        [fileId]: {
          ...state.files[fileId],
          cgSheetProgress: {
            ...state.files[fileId].cgSheetProgress,
            [sheetName]: status,
          },
        },
      },
    })),

  // --- Grouping ---
  createGroup: (fileId1, fileId2) => {
    const groupId = `group_${Date.now()}`;
    set((state) => {
      const file1 = state.files[fileId1];
      const file2 = state.files[fileId2];
      return {
        groups: {
          ...state.groups,
          [groupId]: {
            groupId,
            label: i18n.t('uploadHub:group.defaultName', 'Group {{number}}', {
              number: Object.keys(state.groups).length + 1,
            }),
            fileIds: [fileId1, fileId2],
            groupType: null,
            resolvedSupplierId: null,
            resolvedSupplierName: null,
            resolvedInstallationId: null,
            resolvedInstallationName: null,
          },
        },
        groupOrder: [...state.groupOrder, groupId],
        files: {
          ...state.files,
          [fileId1]: { ...file1, groupId },
          [fileId2]: { ...file2, groupId },
        },
      };
    });
    return groupId;
  },

  addFileToGroup: (fileId, groupId) =>
    set((state) => {
      const group = state.groups[groupId];
      if (!group) return state;
      return {
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            fileIds: [...group.fileIds, fileId],
          },
        },
        files: {
          ...state.files,
          [fileId]: { ...state.files[fileId], groupId },
        },
      };
    }),

  /**
   * Drop files from the batch entirely (P11).
   *
   * The counterpart to `POST /upload-logs/bulk-delete`: the server soft-deletes the records, this
   * removes them from the view. Called after the request succeeds, so a failed delete leaves the
   * rail exactly as it was rather than hiding files that are still there.
   *
   * Group membership is cleaned up the same way `removeFileFromGroup` does — a group that drops
   * below two members auto-dissolves — because a group of one is not a group, and leaving a
   * dangling `groupId` on a surviving file breaks the resolution step.
   */
  removeFiles: (fileIds) =>
    set((state) => {
      const doomed = new Set(fileIds);
      if (doomed.size === 0) return state;

      const files = { ...state.files };
      for (const id of doomed) delete files[id];

      const groups = { ...state.groups };
      let groupOrder = [...state.groupOrder];
      for (const [groupId, group] of Object.entries(groups)) {
        const remaining = (group.fileIds || []).filter((id) => !doomed.has(id));
        if (remaining.length === (group.fileIds || []).length) continue;
        if (remaining.length < 2) {
          for (const id of remaining) {
            if (files[id]) files[id] = { ...files[id], groupId: null };
          }
          delete groups[groupId];
          groupOrder = groupOrder.filter((g) => g !== groupId);
        } else {
          groups[groupId] = { ...group, fileIds: remaining };
        }
      }

      /*
       * The customs batch keeps its OWN copy of the rows, and it is not derived state.
       * `useCustomsBatchState` seeds `customsBatch.rows` once and then bails out of re-deriving
       * (`if (rows || ...) return`) precisely so that manual edits survive a re-render. That
       * means dropping the file here is not enough: without this filter the deleted
       * declaration's lines stay in the grid, keep failing validation, and keep blocking
       * Continue for a document that is no longer in the batch.
       *
       * `null` is left as `null` — it means "not seeded yet", which is different from "seeded
       * and now empty", and flattening the two would re-trigger seeding from files that are
       * already gone.
       */
      const customsBatch =
        state.customsBatch?.rows == null
          ? state.customsBatch
          : {
              ...state.customsBatch,
              rows: state.customsBatch.rows.filter((row) => !doomed.has(row.sourceFileId)),
            };

      return {
        files,
        fileOrder: state.fileOrder.filter((id) => !doomed.has(id)),
        groups,
        groupOrder,
        customsBatch,
        // A deleted file must not stay the grid's filter, or the grid shows nothing and the
        // control that would clear it is gone.
        activeFileId: doomed.has(state.activeFileId) ? null : state.activeFileId,
      };
    }),

  removeFileFromGroup: (fileId) =>
    set((state) => {
      const file = state.files[fileId];
      if (!file?.groupId) return state;

      const groupId = file.groupId;
      const group = state.groups[groupId];
      if (!group) return state;

      const newFileIds = group.fileIds.filter((id) => id !== fileId);

      // Auto-dissolve if fewer than 2 files remain
      if (newFileIds.length < 2) {
        const updatedFiles = { ...state.files };
        updatedFiles[fileId] = { ...updatedFiles[fileId], groupId: null };
        for (const remainingId of newFileIds) {
          updatedFiles[remainingId] = { ...updatedFiles[remainingId], groupId: null };
        }
        const { [groupId]: _, ...remainingGroups } = state.groups;
        return {
          files: updatedFiles,
          groups: remainingGroups,
          groupOrder: state.groupOrder.filter((id) => id !== groupId),
        };
      }

      return {
        groups: {
          ...state.groups,
          [groupId]: { ...group, fileIds: newFileIds },
        },
        files: {
          ...state.files,
          [fileId]: { ...state.files[fileId], groupId: null },
        },
      };
    }),

  dissolveGroup: (groupId) =>
    set((state) => {
      const group = state.groups[groupId];
      if (!group) return state;

      const updatedFiles = { ...state.files };
      for (const fid of group.fileIds) {
        updatedFiles[fid] = { ...updatedFiles[fid], groupId: null };
      }

      const { [groupId]: _, ...remainingGroups } = state.groups;
      return {
        files: updatedFiles,
        groups: remainingGroups,
        groupOrder: state.groupOrder.filter((id) => id !== groupId),
      };
    }),

  // --- Group Type & Propagation ---
  setGroupType: (groupId, groupType) =>
    set((state) => {
      const group = state.groups[groupId];
      if (!group) return state;

      // If changing from same_all to same_supplier, clear propagated installations on uncompleted files
      const updatedFiles = { ...state.files };
      if (group.groupType === GroupType.SAME_ALL && groupType === GroupType.SAME_SUPPLIER) {
        for (const fid of group.fileIds) {
          if (updatedFiles[fid]?.status !== FileStatus.COMPLETED) {
            updatedFiles[fid] = {
              ...updatedFiles[fid],
              resolvedInstallationId: null,
              resolvedInstallationName: null,
              isNewInstallation: false,
            };
          }
        }
      }

      return {
        files: updatedFiles,
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            groupType,
            // Clear group-level installation if switching to same_supplier
            ...(groupType === GroupType.SAME_SUPPLIER
              ? { resolvedInstallationId: null, resolvedInstallationName: null }
              : {}),
          },
        },
      };
    }),

  updateGroupLabel: (groupId, label) =>
    set((state) => {
      const group = state.groups[groupId];
      if (!group) return state;
      return {
        groups: {
          ...state.groups,
          [groupId]: { ...group, label },
        },
      };
    }),

  propagateInstallationToGroup: (groupId, installationId, installationName, isNew) =>
    set((state) => {
      const group = state.groups[groupId];
      if (!group || group.groupType !== GroupType.SAME_ALL) return state;

      const updatedFiles = { ...state.files };
      for (const fid of group.fileIds) {
        if (updatedFiles[fid]?.status !== FileStatus.COMPLETED) {
          updatedFiles[fid] = {
            ...updatedFiles[fid],
            resolvedInstallationId: installationId,
            resolvedInstallationName: installationName,
            isNewInstallation: isNew,
          };
        }
      }

      return {
        files: updatedFiles,
        groups: {
          ...state.groups,
          [groupId]: {
            ...group,
            resolvedInstallationId: installationId,
            resolvedInstallationName: installationName,
          },
        },
      };
    }),

  cancelPendingGroup: (groupId) =>
    set((state) => {
      const group = state.groups[groupId];
      if (!group) return state;

      const updatedFiles = { ...state.files };
      for (const fid of group.fileIds) {
        updatedFiles[fid] = { ...updatedFiles[fid], groupId: null };
      }

      const { [groupId]: _, ...remainingGroups } = state.groups;
      return {
        files: updatedFiles,
        groups: remainingGroups,
        groupOrder: state.groupOrder.filter((id) => id !== groupId),
      };
    }),

  // --- Reorder ---
  reorderFiles: (newFileOrder) => set({ fileOrder: newFileOrder }),

  // --- Detecting flag ---
  setIsDetecting: (val) => set({ isDetecting: val }),

  // --- Files rail ---
  // setRailCollapsed persists; expandRail deliberately does NOT, so the
  // automatic expansions (drag start, newly detected files) restore the
  // user's own preference the next time they collapse it by hand.
  setRailCollapsed: (val) => {
    writeRailCollapsed(val);
    set({ railCollapsed: val });
  },
  toggleRail: () => {
    const next = !get().railCollapsed;
    writeRailCollapsed(next);
    set({ railCollapsed: next });
  },
  expandRail: () => {
    if (get().railCollapsed) set({ railCollapsed: false });
  },

  // --- Hydrate from batch (resume) ---
  hydrateFromBatch: (batchDetail) => {
    const { batch, files: batchFiles } = batchDetail;
    const hydratedFiles = {};
    const fileOrder = [];
    const groups = {};
    const groupOrder = [];

    for (const log of batchFiles) {
      const fileId = log._id;
      const cache = log.detectionCache || {};
      const resolution = log.resolutionState || {};
      const isFailed = log.processingStatus === 'failed';

      // A customs log's template type is stable on the log itself even when
      // `detectionCache.templateType` was never written (the PDF branch only
      // writes `customsExtraction`), so fall back to the upload type (I6).
      const extraction = refuseNonImportDocument(cache.customsExtraction || null);
      const templateType =
        cache.templateType ||
        (extraction || log.uploadType === 'customs_declaration' ? TemplateType.CUSTOMS : 'unknown');

      hydratedFiles[fileId] = {
        fileId,
        filename: log.filename,
        templateType,
        detectedSheets: cache.detectedSheets || null,
        extractedEntity: cache.extractedEntity || null,
        parsedData: cache.parsedData || null,
        error: isFailed
          ? log.processingError || i18n.t('uploadHub:errors.processingFailed', 'Failed')
          : null,
        rawFile: null,
        status:
          FILE_STATUS_BY_LOG_STATUS[log.processingStatus] ??
          (templateType === TemplateType.CUSTOMS
            ? initialFileStatus(templateType, extraction)
            : FileStatus.PENDING),

        extraction,
        firstSeenAt: Date.now(),
        customsState: resolution.customs || null,

        resolvedSupplierId: resolution.resolvedSupplierId || null,
        resolvedSupplierName: resolution.resolvedSupplierName || null,
        resolvedInstallationId: resolution.resolvedInstallationId || null,
        resolvedInstallationName: resolution.resolvedInstallationName || null,
        isNewSupplier: resolution.isNewSupplier || false,
        isNewInstallation: resolution.isNewInstallation || false,
        updateExistingFields: false,
        cgSheetProgress:
          resolution.cgSheetProgress ||
          (cache.detectedSheets
            ? Object.fromEntries(cache.detectedSheets.map((s) => [s.mappedSheetName, 'pending']))
            : null),
        uploadResult: resolution.uploadResult || null,
        groupId: log.groupInfo?.groupId || null,
      };
      fileOrder.push(fileId);

      // Reconstruct groups from groupInfo
      if (log.groupInfo?.groupId) {
        const gid = log.groupInfo.groupId;
        if (!groups[gid]) {
          groups[gid] = {
            groupId: gid,
            label: log.groupInfo.groupLabel || i18n.t('uploadHub:group.fallbackName', 'Group'),
            fileIds: [],
            groupType: log.groupInfo.groupType || null,
            resolvedSupplierId: null,
            resolvedSupplierName: null,
            resolvedInstallationId: null,
            resolvedInstallationName: null,
          };
          groupOrder.push(gid);
        }
        groups[gid].fileIds.push(fileId);
      }
    }

    // Settled = nothing left to do for the file. (Deliberately includes ERROR,
    // unlike advanceToNextFile: a reopened batch shows a failed file in the summary.)
    const isSettled = (fid) =>
      [FileStatus.COMPLETED, FileStatus.ERROR, FileStatus.SKIPPED].includes(
        hydratedFiles[fid]?.status,
      );

    const firstPending = fileOrder.find((fid) => !isSettled(fid));
    const allDone = firstPending === undefined;

    // A customs review the user had already opened, with nothing created yet,
    // is resumed IN the review rather than on the per-file panel one click
    // before it (D47). A review never opened lands where it always did.
    const reviewStarted = fileOrder.some((fid) => {
      const file = hydratedFiles[fid];
      return (
        file?.templateType === TemplateType.CUSTOMS &&
        file.status !== FileStatus.COMPLETED &&
        file.extraction?.status === ExtractionStatus.READY &&
        file.customsState?.batch === true &&
        !file.customsState?.created
      );
    });

    writeOpenBatchId(batch._id);
    set({
      batchId: batch._id,
      files: hydratedFiles,
      fileOrder,
      groups,
      groupOrder,
      activeFileId: firstPending || fileOrder[0] || null,
      phase: allDone ? Phase.SUMMARY : reviewStarted ? Phase.CUSTOMS_BATCH : Phase.RESOLUTION,
      view: UploadHubView.UPLOAD,
      isDetecting: false,
      // Re-seeded from the hydrated per-file `resolutionState.customs` slices
      // the first time the batch wizard mounts (I6 — no new backend field).
      customsBatch: emptyCustomsBatch(),
      customsSave: { status: 'idle', at: null },
    });
  },

  // --- Reset ---
  // A new upload session: this tab no longer has a batch to reopen (D48).
  reset: () => {
    writeOpenBatchId(null);
    set({
      phase: Phase.DROPZONE,
      view: UploadHubView.UPLOAD,
      batchId: null,
      files: {},
      fileOrder: [],
      groups: {},
      groupOrder: [],
      activeFileId: null,
      isDetecting: false,
      customsBatch: emptyCustomsBatch(),
      customsSave: { status: 'idle', at: null },
      goodsJumpDismissed: false,
    });
  },

  // A batch the hub has loaded is gone — history-deleted here or elsewhere — so
  // the hub must never attach files to it again (upload-batch-soft-delete D9).
  // The tab is kept: deleting from History must not throw the user onto Files.
  forgetBatch: (id) => {
    if (!id || get().batchId !== id) return;
    const { view } = get();
    get().reset();
    set({ view });
  },
}));

/**
 * Drop everything the Upload Hub holds for the signed-in organisation: its
 * files, review progress, save state and this tab's remembered batch.
 *
 * The store is a module singleton and sign-out → sign-in is an SPA transition
 * with no reload, so without this the next account — another declarant — saw
 * the previous one's files and review, and the tab reopened the previous one's
 * batch by id. Called at the app's two cache boundaries, beside
 * `queryClient.clear()`: sign-out (`_app.jsx`) and a real organisation switch
 * (`OrgBoundary.jsx`).
 */
export const resetUploadHubSession = () => useUploadHubStore.getState().reset();

export default useUploadHubStore;
